import React, { useEffect, useState } from "react";
import StringUtils from "../utils/StringUtils";
import EmailUtils from "../utils/EmailUtils";
import ErrorUtils from "../utils/ErrorUtils";
import TrustdService from "../service/TrustdService";
import CheckScoreResults from "./CheckScoreResults";
import { Button, Card, Field } from "../components/UI";
import { useToast } from "../hooks";

export default function QuickScoreResults({ reference, query }) {
  const directShareToken = String(query?.st || "").trim();
  const legacyShareEmail = String(query?.email || "").trim();
  const [trustdReference, setTrustdReference] = useState(reference || "");
  const [hostEmail, setHostEmail] = useState(legacyShareEmail);
  const [shareToken, setShareToken] = useState(directShareToken);
  const [guest, setGuest] = useState(null);
  const [booking, setBooking] = useState(null);
  const [scoreResult, setScoreResult] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [displayResult, setDisplayResult] = useState(false);
  const [isRetrieving, setIsRetrieving] = useState(false);
  const addToast = useToast();

  useEffect(() => {
    if (reference) setTrustdReference(reference);
    setShareToken(directShareToken);
    setHostEmail(legacyShareEmail);
    if (reference && directShareToken) {
      retrieveScore({ token: directShareToken, ref: reference });
      return;
    }
    if (reference && legacyShareEmail) {
      retrieveScore({ email: legacyShareEmail, ref: reference });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference, directShareToken, legacyShareEmail]);

  const validateReference = (ref = trustdReference) => {
    let valid = true;
    if (StringUtils.isEmpty(ref)) {
      addToast({ severity: "error", summary: "Validation Error", detail: "Trustd Reference is required", life: 5000 });
      valid = false;
    }
    return valid;
  };

  const validateEmail = (email = hostEmail) => {
    let valid = true;
    if (StringUtils.isEmpty(email)) {
      addToast({ severity: "error", summary: "Validation Error", detail: "Host Email Address is required", life: 5000 });
      valid = false;
    }
    if (!StringUtils.isEmpty(email) && !EmailUtils.isValid(email)) {
      addToast({ severity: "error", summary: "Validation Error", detail: "Host Email is not a valid email address", life: 5000 });
      valid = false;
    }
    return valid;
  };

  const retrieveScore = async ({ email = hostEmail, ref = trustdReference, token = shareToken } = {}) => {
    if (isRetrieving) return;
    if (!validateReference(ref)) return;
    const normalizedToken = String(token || "").trim();
    const normalizedEmail = String(email || "").trim();
    if (!normalizedToken && !validateEmail(normalizedEmail)) return;
    try {
      setIsRetrieving(true);
      const result = normalizedToken
        ? await TrustdService.getScoreBySharedToken(ref, normalizedToken)
        : await TrustdService.getScoreByHostEmailAndReference(normalizedEmail, ref);

      setGuest(result.payload);
      setBooking(result.payload);
      setScoreResult(result.response);
      setGeneratedAt(result.createdAt);
      setDisplayResult(true);
    } catch (error) {
      addToast({ severity: "error", summary: "Error", detail: ErrorUtils.parseError(error), life: 10000 });
    } finally {
      setIsRetrieving(false);
    }
  };

  const onEnter = (event) => {
    if (event.key === "Enter") {
      if (shareToken) {
        retrieveScore({ token: shareToken });
        return;
      }
      if (!validateEmail()) return;
      retrieveScore();
    }
  };

  const refreshResults = async () => {
    const newScoreResult = await TrustdService.refreshMatchApiResultsResponse(scoreResult.trustdReference);
    if (newScoreResult) setScoreResult(newScoreResult);
    return newScoreResult;
  };

  return (
    <div className="quickscore-page quickscore-modern">
      {!displayResult ? (
        <div className="quickscore-shell">
          <div className="quickscore-hero">
            <p className="dashboard-section-kicker">Trustd Secure Report</p>
            <h1>Quick Score Results</h1>
            <p className="quickscore-subtitle">
              Access your guest screening report via secure link or with host email and score ID.
            </p>
          </div>

          <Card className="quickscore-form-card">
            {shareToken ? (
              <div className="quickscore-token-mode">
                <p className="quickscore-token-title">Secure token link detected.</p>
                <p className="quickscore-token-copy">
                  The report can be opened without exposing host email in the URL.
                </p>
                <Field label="Score ID" required>
                  <input
                    className="form-control"
                    placeholder="Trustd Reference"
                    value={trustdReference}
                    autoCapitalize="characters"
                    spellCheck="false"
                    onKeyDown={onEnter}
                    onChange={(event) => setTrustdReference(event.target.value)}
                  />
                </Field>
                <Button
                  label={isRetrieving ? "Retrieving..." : "Open Secure Report"}
                  disabled={isRetrieving}
                  onClick={() => retrieveScore({ token: shareToken })}
                />
              </div>
            ) : (
              <div className="quickscore-manual-mode">
                <Field label="Host Email Address" required>
                  <input
                    type="email"
                    className="form-control"
                    placeholder="Host / Property Manager Email Address"
                    value={hostEmail}
                    autoComplete="email"
                    autoCapitalize="none"
                    spellCheck="false"
                    onKeyDown={onEnter}
                    onChange={(event) => setHostEmail(event.target.value)}
                  />
                </Field>
                <Field label="Score ID" required>
                  <input
                    className="form-control"
                    placeholder="Trustd Reference"
                    value={trustdReference}
                    autoCapitalize="characters"
                    spellCheck="false"
                    onKeyDown={onEnter}
                    onChange={(event) => setTrustdReference(event.target.value)}
                  />
                </Field>
                <Button
                  label={isRetrieving ? "Retrieving..." : "Retrieve"}
                  disabled={isRetrieving}
                  onClick={() => {
                    if (!validateEmail()) return;
                    retrieveScore();
                  }}
                />
              </div>
            )}
          </Card>
        </div>
      ) : (
        <Card className="checkscore-result-shell quickscore-result-card">
          <CheckScoreResults
            guest={guest}
            booking={booking}
            scoreResult={scoreResult}
            generatedAt={generatedAt}
            refreshResults={refreshResults}
          />
        </Card>
      )}
    </div>
  );
}
